import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';
import './Legal.css';

const Privacy: React.FC = () => {
  return (
    <div className="legal-page">
      <div className="legal-header">
        <div className="container">
          <Link to="/" className="back-link">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
          <div className="legal-title-section">
            <Shield size={48} className="legal-icon" />
            <h1>Privacy Policy</h1>
            <p className="legal-subtitle">Last updated: January 2024</p>
          </div>
        </div>
      </div>

      <div className="legal-content">
        <div className="container">
          <div className="legal-body">
            <section>
              <h2>1. Introduction</h2>
              <p>
                Welcome to Flazu API ("we," "our," or "us"). We respect your privacy and are committed to protecting your personal data. 
                This privacy policy explains how we collect, use, disclose, and safeguard your information when you use our API gateway service.
              </p>
            </section>

            <section>
              <h2>2. Information We Collect</h2>
              <h3>2.1 Account Information</h3>
              <p>When you register for an account, we collect:</p>
              <ul>
                <li>Full name</li>
                <li>Email address</li>
                <li>Password (encrypted)</li>
                <li>API keys and usage data</li>
              </ul>

              <h3>2.2 Usage Data</h3>
              <p>We automatically collect information about your API usage:</p>
              <ul>
                <li>API requests and responses</li>
                <li>IP addresses</li>
                <li>Request timestamps</li>
                <li>Token usage statistics</li>
                <li>Error logs</li>
              </ul>

              <h3>2.3 Technical Data</h3>
              <ul>
                <li>Browser type and version</li>
                <li>Device information</li>
                <li>Operating system</li>
                <li>Time zone settings</li>
              </ul>
            </section>

            <section>
              <h2>3. How We Use Your Information</h2>
              <p>We use your personal data for the following purposes:</p>
              <ul>
                <li><strong>Service Delivery:</strong> To provide and maintain our API gateway service</li>
                <li><strong>Account Management:</strong> To manage your account and subscriptions</li>
                <li><strong>Usage Analytics:</strong> To monitor and analyze service usage and performance</li>
                <li><strong>Security:</strong> To detect and prevent fraud, abuse, and security incidents</li>
                <li><strong>Communication:</strong> To send you service updates, security alerts, and support messages</li>
                <li><strong>Compliance:</strong> To comply with legal obligations and enforce our terms</li>
              </ul>
            </section>

            <section>
              <h2>4. Data Sharing and Disclosure</h2>
              <h3>4.1 Third-Party Services</h3>
              <p>We may share your data with:</p>
              <ul>
                <li><strong>AI Model Providers:</strong> OpenAI, Anthropic, Google, etc. (only API requests)</li>
                <li><strong>Payment Processors:</strong> For handling subscriptions and payments</li>
                <li><strong>Analytics Providers:</strong> For service improvement and monitoring</li>
                <li><strong>Cloud Infrastructure:</strong> AWS, MongoDB Atlas for data storage</li>
              </ul>

              <h3>4.2 Legal Requirements</h3>
              <p>We may disclose your information if required by law or to:</p>
              <ul>
                <li>Comply with legal processes</li>
                <li>Enforce our terms and policies</li>
                <li>Protect our rights, property, or safety</li>
                <li>Prevent fraud or security threats</li>
              </ul>
            </section>

            <section>
              <h2>5. Data Security</h2>
              <p>We implement industry-standard security measures:</p>
              <ul>
                <li>SSL/TLS encryption for data in transit</li>
                <li>Encrypted storage for sensitive data</li>
                <li>Regular security audits and monitoring</li>
                <li>Access controls and authentication</li>
                <li>IP whitelisting and rate limiting</li>
              </ul>
              <p>
                However, no method of transmission over the Internet is 100% secure. 
                We cannot guarantee absolute security of your data.
              </p>
            </section>

            <section>
              <h2>6. Data Retention</h2>
              <p>We retain your personal data for as long as necessary to:</p>
              <ul>
                <li>Provide our services</li>
                <li>Comply with legal obligations</li>
                <li>Resolve disputes</li>
                <li>Enforce our agreements</li>
              </ul>
              <p>Usage logs and analytics data are typically retained for 90 days.</p>
            </section>

            <section>
              <h2>7. Your Rights</h2>
              <p>Under applicable data protection laws, you have the right to:</p>
              <ul>
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update inaccurate or incomplete data</li>
                <li><strong>Deletion:</strong> Request deletion of your data</li>
                <li><strong>Portability:</strong> Receive your data in a portable format</li>
                <li><strong>Object:</strong> Object to processing of your data</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
              </ul>
              <p>To exercise these rights, contact us at: <a href="mailto:privacy@flazu.com">privacy@flazu.com</a></p>
            </section>

            <section>
              <h2>8. Cookies and Tracking</h2>
              <p>We use cookies and similar technologies to:</p>
              <ul>
                <li>Maintain your session and authentication</li>
                <li>Remember your preferences</li>
                <li>Analyze service usage</li>
                <li>Improve user experience</li>
              </ul>
              <p>See our <Link to="/cookies">Cookie Policy</Link> for more details.</p>
            </section>

            <section>
              <h2>9. International Data Transfers</h2>
              <p>
                Your data may be transferred to and processed in countries other than Malaysia. 
                We ensure appropriate safeguards are in place to protect your data in accordance with this policy.
              </p>
            </section>

            <section>
              <h2>10. Children's Privacy</h2>
              <p>
                Our service is not intended for users under 18 years of age. 
                We do not knowingly collect personal data from children. 
                If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2>11. Changes to This Policy</h2>
              <p>
                We may update this privacy policy from time to time. We will notify you of any material changes by:
              </p>
              <ul>
                <li>Posting the new policy on this page</li>
                <li>Updating the "Last updated" date</li>
                <li>Sending you an email notification (for significant changes)</li>
              </ul>
            </section>

            <section>
              <h2>12. Contact Us</h2>
              <p>If you have questions about this Privacy Policy, please contact us:</p>
              <ul>
                <li>Email: <a href="mailto:privacy@flazu.com">privacy@flazu.com</a></li>
                <li>Support: <a href="mailto:support@flazu.com">support@flazu.com</a></li>
              </ul>
            </section>

            <section>
              <h2>13. Malaysian Privacy Law Compliance</h2>
              <p>
                We comply with the Personal Data Protection Act 2010 (PDPA) of Malaysia. 
                Your personal data is processed in accordance with the PDPA principles.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Privacy;